import { FORM_SCHEMA_TYPES as TYPES } from "../constants/schemaGenerator/formSchemaTypes";
import { get } from "lodash";

/**
 * Country codes for state field logic
 */
export const COUNTRY_CODES = {
  USA: "USA",
  CANADA: "CAN",
};

/**
 * Passport type codes for DS 160 form
 * Values come from backend via schemaData.PASSPORT_TYPE
 */
export const PASSPORT_TYPE_CODES = {
  REGULAR: "R", // Requires expiry date
  DIPLOMATIC: "D", // May not expire
  OFFICIAL: "O", // May not expire
  SERVICE: "S", // May not expire
  TEMPORARY: "T", // May not expire, requires explanation
};

/**
 * Gets the country from the applicant data based on the address path
 * @param {Object} applicant - The applicant data object
 * @param {string} path - The path to the address object
 * @returns {string|undefined} The country code
 */
export const getCountry = (applicant, path) => {
  return get(applicant, `${path}.country`);
};

/**
 * Gets the passport type from the applicant data
 * @param {Object} applicant - The applicant data object
 * @returns {string|undefined} The passport type code (R, D, O, S, T)
 */
export const getPassportType = (applicant) => {
  return get(applicant, "passportType");
};

/**
 * Gets the appropriate state values based on the selected country
 * @param {string} country - The country code (e.g., "USA", "CAN")
 * @param {string} americanStatesConstant - The constant key for American states
 * @param {string} canadaStatesConstant - The constant key for Canada states/provinces
 * @returns {string} The constant key for the appropriate state list
 */
export const getStateValuesByCountry = (
  country,
  americanStatesConstant,
  canadaStatesConstant,
) => {
  const statesByCountry = {
    [COUNTRY_CODES.USA]: americanStatesConstant,
    [COUNTRY_CODES.CANADA]: canadaStatesConstant,
  };
  return statesByCountry[country] || americanStatesConstant;
};

/**
 * Determines if the state field should be shown based on the selected country
 * @param {string} country - The country code (e.g., "USA", "CAN")
 * @returns {boolean} True if country is USA or CAN, false otherwise
 */
export const shouldShowStateField = (country) => {
  return country === COUNTRY_CODES.USA || country === COUNTRY_CODES.CANADA;
};

/**
 * Creates an onChange handler that resets the state field
 * @param {string} path - The path to the address object
 * @returns {Function} onChange handler function
 */
export const resetState = (path) => {
  return (newValue, changeState, applicant, schemaData) => {
    changeState("state", undefined, path);
  };
};

/**
 * Creates a dynamic state field that changes its options based on the selected country
 * @param {string} path - The path to the address object (e.g., "permanentAddress", "emergencyContact.address")
 * @param {string} americanStatesConstant - The constant key for American states (e.g., "AMERICAN_STATES")
 * @param {string} canadaStatesConstant - The constant key for Canada states/provinces (e.g., "CANADA_STATES")
 * @returns {Object} A schema field configuration for the state select
 */
export const createDynamicStateField = (
  path,
  americanStatesConstant,
  canadaStatesConstant,
) => ({
  label: "State",
  type: TYPES.SELECT,
  values: (applicant) => {
    const country = getCountry(applicant, path);
    return getStateValuesByCountry(
      country,
      americanStatesConstant,
      canadaStatesConstant,
    );
  },
  path,
  customDependency: (applicant) => {
    const country = getCountry(applicant, path);
    return shouldShowStateField(country);
  },
});

/**
 * Check if passport type requires expiry date
 * @param {string} passportType - The passport type code
 * @returns {boolean} True for Regular (R), false for others
 */
export const requiresPassportExpiryDate = (passportType) => {
  return passportType === PASSPORT_TYPE_CODES.REGULAR;
};

const SSN_VALIDATION_KEYWORD = "Social Security Number";

/**
 * Formats a 9-digit string into SSN format (XXX-XX-XXXX)
 * @param {string} digits - The raw digits string
 * @returns {string} Formatted SSN or empty string if not exactly 9 digits
 */
const formatSSN = (digits) => {
  if (digits.length !== 9) return "";
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

/**
 * Creates an SSN field definition with validation error visibility logic
 * and automatic formatting into the USSocialSecurityNumber field.
 *
 * The field is visible when the applicant has an ssn value or when
 * there is a validation error mentioning "Social Security Number".
 *
 * @returns {Object} A schema field configuration for the SSN input
 */
export const createSSNField = () => ({
  label: SSN_VALIDATION_KEYWORD,
  type: TYPES.TEXT,
  allowEditSecret: false,
  customDependency: (applicant) => {
    if (applicant.ssn) return true;
    const validationErrors = get(applicant, "validationErrorList") || [];
    return validationErrors.some(
      (error) => typeof error === "string" && error.includes(SSN_VALIDATION_KEYWORD),
    );
  },
  onChange: (newValue, changeState, applicant, schemaData) => {
    const digits = (newValue || "").replace(/\D/g, "");
    changeState("USSocialSecurityNumber", formatSSN(digits));
  },
});
